import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, inject, Output, signal, ViewChild } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { IVoucherType } from '../../core/interfaces/IVoucherType';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { CartService } from '../../../cart/core/services/CartService';
import { ApiErrorCodes } from '../../../../core/enums/ErrorCodes';
import { AddItemsToCart } from '../../../../core/types';
import { EMPTY, finalize, from, map, switchMap, take } from 'rxjs';
import { VouchersService } from '../../core/services/VouchersService';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import dayjs from 'dayjs';
import { IVoucherDetails } from '../../core/interfaces/IVoucherDetails';
import { IVoucherTransaction } from '../../core/interfaces/IVoucherTransaction';
import { ICartItem } from '../../../cart/core/interfaces/ICartItem';
import { ExternalVoucherTicketPickerModalComponent } from '../external-voucher-ticket-picker-modal/external-voucher-ticket-picker-modal.component';
import { externalVoucherTicketPickerDecision } from '../../core/utils/externalVoucherTicketPickerDecision';
import { filterTicketsConsumedByExistingExternalVouchers } from '../../core/utils/filterTicketsConsumedByExistingExternalVouchers';
import { CardsService } from '../../../cards/core/services/CardsService';
import { ICard } from '../../../cards/core/interfaces/ICard';

@Component({
  selector: 'app-voucher-code-input-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    ModalComponent,
    FormControlComponent,
    FormsModule,
    ReactiveFormsModule,
    ExternalVoucherTicketPickerModalComponent
  ],
  templateUrl: './voucher-code-input-modal.component.html',
  styleUrl: './voucher-code-input-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VoucherCodeInputModalComponent extends BaseModalComponent<IVoucherType> {

  @ViewChild('codeInput') public codeInput: ElementRef<HTMLInputElement>;
  @ViewChild('externalVoucherTicketPickerModal') private _externalVoucherTicketPickerModal: ExternalVoucherTicketPickerModalComponent;

  @Output() public codeSubmitted = new EventEmitter<{ voucherNumber: string; voucherDetails?: IVoucherDetails; transactions?: IVoucherTransaction[]; actionType?: 'details' | 'cancel' | 'invalidate' }>();
  @Output() public cardDetailsRequested = new EventEmitter<ICard>();

  public static instance: VoucherCodeInputModalComponent;
  public code: string;
  public actionType: 'details' | 'cancel' | 'invalidate' | null = null;
  public readonly isLoading = signal(false);

  private readonly _cartService = inject(CartService);
  private readonly _cartStore = inject(CartStore);
  private readonly _vouchersService = inject(VouchersService);
  private readonly _cardsService = inject(CardsService);
  private readonly _destroyRef = inject(DestroyRef);

  public override open(val?: IVoucherType, actionType?: 'details' | 'cancel' | 'invalidate', code?: string): void {
    VoucherCodeInputModalComponent.instance = this;
    this.code = code || null;
    this.actionType = actionType || null;
    this.isLoading.set(false);

    if (this.code) {
      this.onSubmit();
    } else {
      super.open(val);
      this.codeInput?.nativeElement?.focus();
    }
  }

  public override close(): void {
    VoucherCodeInputModalComponent.instance = null;
    super.close();
  }

  public onSubmit(): void {
    if (!this.code) {
      return;
    }

    if (this.data?.flagAcardo) {
      this.validateAndAddAcardo();
      return;
    }

    if (this.actionType === 'details') {
      this.loadVoucherDetails();

      return;
    }

    if (this.actionType === 'cancel') {
      this.addCancelVoucherToCart();

      return;
    }

    if (this.actionType === 'invalidate') {
      this.invalidateVoucher();
      return;
    }

    this.addVoucherToCart();
  }

  public addCancelVoucherToCart(): void {
    if (!this.code) {
      return;
    }

    this.isLoading.set(true);

    this._vouchersService.getVoucherByNumber(this.code)
      .subscribe({
        next: (response) => {
          if (response.data?.voucherId) {
            const items: AddItemsToCart = [
              {
                itemId: response.data?.voucherId,
                itemType: CartItemTypes.CancelVoucher,
                voucherNumber: this.code,
                quantity: 1
              }
            ];

            this._cartService.addItemToCart(items)
              .pipe(
                finalize(() => this.isLoading.set(false))
              )
              .subscribe({
                next: () => {
                  this.close();
                },
                error: ({ error }: ApiErrorResponse) => {
                  this.notificationService.pushError(
                    'Fehler'.trans('notifications/error.Title'),
                    error.message.trans(`add-cancel-voucher-to-cart/notifications/${error.message}.Message`)
                  );
                }
              });
          }
        },
        error: ({ error }: ApiErrorResponse) => {
          this.isLoading.set(false);
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`invalidate-voucher/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public invalidateVoucher(): void {
    if (!this.code) {
      return;
    }

    this._vouchersService.invalidateVoucher(this.code)
      .subscribe({
        next: res => {
          this.notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'Voucher successfully invalidated'.trans(`invalidate-voucher/notifications/success.Message`)
          )
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`invalidate-voucher/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public addVoucherToCart(): void {
    this.isLoading.set(true);

    if (this.data?.voucherTypeId) {
      const items: AddItemsToCart = [
        {
          itemId: this.data.voucherTypeId,
          itemType: CartItemTypes.Voucher,
          voucherNumber: this.code,
          quantity: 1
        }
      ];

      this._cartService.addItemToCart(items, { suppressedMessages: [ApiErrorCodes.VoucherNumberBelongsToCard] })
        .pipe(
          finalize(() => this.isLoading.set(false)),
        )
        .subscribe({
          next: res => {
            if (res.message === ApiErrorCodes.VoucherNumberBelongsToCard) {
              this.openAttachCardInsteadConfirmation();
              return;
            }

            if (!(res.message in ApiErrorCodes)) {
              this.close();
            }
          },
          error: ({ error }: ApiErrorResponse) => {
            if (error.message === ApiErrorCodes.VoucherNumberBelongsToCard) {
              this.openAttachCardInsteadConfirmation();
              return;
            }

            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`add-voucher-item-to-cart/notifications/${error.message}.Message`)
            );
          }
        });
    } else {
      this._vouchersService.getVoucherByNumber(this.code)
        .pipe(
          switchMap((voucherResponse) => {
            const voucherInfo = voucherResponse.data;

            if (voucherInfo?.validUntil) {
              const validUntilDate = dayjs(voucherInfo.validUntil);
              const now = dayjs();

              if (validUntilDate.isBefore(now)) {
                const formattedDate = validUntilDate.format('DD-MM-YYYY');
                const confirmationText = `This voucher is expired since: <b>[[formattedDate]]</b><br>Do you still want to redeem it?`.trans('vouchers/modals/voucher-code-input-modal/voucher-expired-confirmation.Message', [['formattedDate', formattedDate]]);

                return from(
                  new Promise<boolean>((resolve) => {
                    let resolved = false;

                    ConfirmationModalComponent.openConfirmationModal(
                      'Voucher Expired'.trans('vouchers/modals/voucher-code-input-modal/voucher-expired-confirmation.Title'),
                      confirmationText,
                      this._destroyRef,
                      () => {
                        if (!resolved) {
                          resolved = true;
                          resolve(true);
                        }
                      }
                    );

                    ConfirmationModalComponent.instance.onCancel
                      .pipe(take(1))
                      .subscribe(() => {
                        if (!resolved) {
                          resolved = true;
                          resolve(false);
                        }
                      });
                  })
                ).pipe(
                  switchMap((confirmed) => {
                    if (confirmed) {
                      const items: AddItemsToCart = [
                        {
                          itemId: 0,
                          itemType: CartItemTypes.CashVoucher,
                          voucherNumber: this.code,
                          quantity: 1
                        }
                      ];
                      return this._cartService.addItemToCart(items, { suppressedMessages: [ApiErrorCodes.VoucherNumberBelongsToCard] });
                    } else {
                      this.close();
                    }

                    return EMPTY;
                  })
                );
              }
            }

            const items: AddItemsToCart = [
              {
                itemId: 0,
                itemType: CartItemTypes.CashVoucher,
                voucherNumber: this.code,
                quantity: 1
              }
            ];

            return this._cartService.addItemToCart(items, { suppressedMessages: [ApiErrorCodes.VoucherNumberBelongsToCard] });
          }),
          finalize(() => this.isLoading.set(false)),
        )
        .subscribe({
          next: res => {
            if (res?.message === ApiErrorCodes.VoucherNumberBelongsToCard) {
              this.openAttachCardInsteadConfirmation();
              return;
            }

            if (res && !(res.message in ApiErrorCodes)) {
              this.close();
            }
          },
          error: ({ error }: ApiErrorResponse) => {
            if (error.message === ApiErrorCodes.VoucherNumberBelongsToCard) {
              this.openAttachCardInsteadConfirmation();
              return;
            }

            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`cash-voucher-in-cart/notifications/${error.message}.Message`)
            );
          }
        });
    }
  }

  public onAcardoTicketSelected(ticket: ICartItem): void {
    this.executeAcardoValidation(ticket);
  }

  private openAttachCardInsteadConfirmation(): void {
    ConfirmationModalComponent.openConfirmationModal(
      'Card number'.trans('vouchers/modals/voucher-code-input-modal/attach-card-confirmation.Title'),
      'This number belongs to a card. Attach card instead?'.trans('vouchers/modals/voucher-code-input-modal/attach-card-confirmation.Message'),
      this._destroyRef,
      () => this.attachCardInstead()
    );
  }

  private attachCardInstead(): void {
    this._cartService.attachCardToCart(this.code)
      .subscribe({
        next: (res) => {
          if (!(res.message in ApiErrorCodes)) {
            this.close();
          }
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`attach-card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private loadVoucherDetails(): void {
    if (!this.code) {
      return;
    }

    this.isLoading.set(true);

    this._vouchersService.getVoucherByNumber(this.code)
      .pipe(
        switchMap((voucherResponse) => this._vouchersService.getVoucherTransactionsByNumber(this.code)
          .pipe(
            map((transactionsResponse) => [voucherResponse, transactionsResponse] as const)
          )
        ),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ([voucherResponse, transactionsResponse]) => {
          if (voucherResponse.data) {
            const voucherDetails: IVoucherDetails = {
              voucherId: voucherResponse.data.voucherId,
              voucherNumber: voucherResponse.data.voucherNr || this.code,
              balance: voucherResponse.data.amount || 0,
              status: voucherResponse.data.voucherStatus || '',
              validUntil: voucherResponse.data.validUntil || null,
              name: voucherResponse.data.name || '',
              cancelInfo: voucherResponse.data.cancelInfo || ''
            };

            this.codeSubmitted.emit({
              voucherNumber: this.code,
              voucherDetails: voucherDetails,
              transactions: transactionsResponse.data,
              actionType: 'details'
            });
          }
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          if (error.message === ApiErrorCodes.VoucherNumberBelongsToCard) {
            this.openCardDetailsConfirmation();
            return;
          }

          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`get-voucher-details/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private openCardDetailsConfirmation(): void {
    ConfirmationModalComponent.openConfirmationModal(
      'Card number'.trans('vouchers/modals/voucher-code-input-modal/card-found-confirmation.Title'),
      'This number belongs to a card. Open card details?'.trans('vouchers/modals/voucher-code-input-modal/card-found-confirmation.Message'),
      this._destroyRef,
      () => this.openCardDetails()
    );
  }

  private openCardDetails(): void {
    this.isLoading.set(true);

    this._cardsService.getCardByNumber(this.code)
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ({ data }) => {
          this.close();
          this.cardDetailsRequested.emit(data);
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private validateAndAddAcardo(): void {
    const tickets = this._cartStore.cartItemsTickets();
    const externalVouchers = this._cartStore.cartItemsExternalVouchers();
    const availableTickets = filterTicketsConsumedByExistingExternalVouchers(tickets, externalVouchers);
    const decision = externalVoucherTicketPickerDecision(availableTickets);

    if (!decision.canContinueWithoutPicker && decision.reason === 'no-tickets') {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'Add a ticket to the cart before using this voucher.'.trans('vouchers/external-voucher-ticket-picker-modal/no-tickets-error.Message')
      );
      return;
    }

    if (decision.canContinueWithoutPicker) {
      this.executeAcardoValidation(availableTickets[0]);
      return;
    }

    this._externalVoucherTicketPickerModal.open(availableTickets);
  }

  private executeAcardoValidation(ticket: ICartItem): void {
    this.isLoading.set(true);

    const showId = ticket.showId > 0 ? ticket.showId : null;
    const valueCents = Math.round(Math.abs(ticket.totalPrice) * 100);

    this._vouchersService.validateAcardoVoucher(
      this.data.voucherTypeId,
      this.code,
      showId,
      valueCents
    ).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: ({ data }) => {
        if (!data.isValid) {
          const errorKey = data.message ?? 'AcardoVoucherInvalid';
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            errorKey.trans(`vouchers/external/acardo/notifications/${errorKey}.Message`)
          );
          return;
        }
        this.addExternalVoucherToCart(data.voucherCode, data.voucherAmount, showId, data.externalCouponCode);
      },
      error: ({ error }: ApiErrorResponse) => {
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`add-voucher-item-to-cart/notifications/${error.message}.Message`)
        );
      }
    });
  }

  private addExternalVoucherToCart(voucherNumber: string, price: number, showId: number, externalCouponCode?: number): void {
    const items: AddItemsToCart = [
      {
        itemId: this.data.voucherTypeId,
        itemType: CartItemTypes.ExternalVoucher,
        showId,
        voucherNumber,
        externalCouponCode,
        price,
        quantity: 1
      }
    ];

    this._cartService.addItemToCart(items).subscribe({
      next: () => this.close(),
      error: ({ error }: ApiErrorResponse) => {
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`add-voucher-item-to-cart/notifications/${error.message}.Message`)
        );
      }
    });
  }
}
